import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

/**
 * Gating for preloading a stock tab from the overview (ortex/ortex-gui#1350).
 *
 * The preload must never compete with the page the reader is on: it waits for start-up to finish
 * (`window.__ocAfterStartup`, src/pages/_document.tsx), for the user request, and for the app's API
 * requests to go quiet, and it is skipped on a data-saver or 2G connection.
 */

export interface ConnectionInfo {
    saveData?: boolean;
    effectiveType?: string;
}

/** False when the reader has asked to save data or the connection is 2G or slower. */
export const connectionAllowsPreload = (connection: ConnectionInfo | undefined | null): boolean => {
    if (!connection) return true;
    if (connection.saveData) return false;
    return !["slow-2g", "2g"].includes(connection.effectiveType ?? "");
};

export interface PreloadPlan {
    /** The route's code (fetched, not run: `prefetchPageFiles`). */
    code: boolean;
    /** The route's `_next/data` JSON. */
    data: boolean;
    /** The API calls the tab makes on mount: signed-in readers only. */
    api: boolean;
}

/**
 * What to preload, or null for nothing. `subscriptionPlan` is undefined until the user request has
 * answered; nothing is preloaded before that.
 */
export const preloadPlan = (connection: ConnectionInfo | undefined | null, subscriptionPlan: string | undefined): PreloadPlan | null => {
    if (!subscriptionPlan) return null;
    if (!connectionAllowsPreload(connection)) return null;
    return { code: true, data: true, api: subscriptionPlan !== "ORTEX Visitor" };
};

// ---- API quiet --------------------------------------------------------------------------------

const TRACKED = Symbol("ocInflightTracked");
type Tracked = InternalAxiosRequestConfig & { [TRACKED]?: boolean };

let inflight = 0;
let lastSettled = 0;
const installed = new WeakSet<AxiosInstance>();

/**
 * Counts the requests in flight on an axios instance (the global one by default, which every
 * controller uses). Installed once, when this module loads with the overview page: before the page's
 * own effects send their requests.
 */
export const trackApiInflight = (instance: AxiosInstance = axios) => {
    if (installed.has(instance)) return;
    installed.add(instance);
    instance.interceptors.request.use((config: Tracked) => {
        config[TRACKED] = true;
        inflight += 1;
        return config;
    });
    const settle = (config: Tracked | undefined) => {
        if (config?.[TRACKED]) {
            inflight = Math.max(0, inflight - 1);
            lastSettled = Date.now();
        }
    };
    instance.interceptors.response.use(
        (res) => {
            settle(res.config as Tracked);
            return res;
        },
        (err) => {
            settle(err?.config as Tracked | undefined);
            return Promise.reject(err);
        }
    );
};

export const apiInflight = () => inflight;

/**
 * Resolves true once no tracked API request has been in flight for `quietMs`, or false when that has
 * not happened within `ceilingMs` (a page that never goes quiet gets no preload).
 */
export const whenApiQuiet = ({ quietMs = 500, ceilingMs = 15000, pollMs = 100 }: { quietMs?: number; ceilingMs?: number; pollMs?: number } = {}): Promise<boolean> =>
    new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            const now = Date.now();
            if (inflight === 0 && now - Math.max(lastSettled, start) >= quietMs) return resolve(true);
            if (now - start >= ceilingMs) return resolve(false);
            setTimeout(check, pollMs);
        };
        check();
    });

/** Runs `fn` once start-up has finished (first interaction, or idle after load with a 4 s ceiling). */
export const afterStartup = (fn: () => void) => {
    const w = window as Window & { __ocAfterStartup?: (g: () => void) => void };
    if (typeof w.__ocAfterStartup === "function") w.__ocAfterStartup(fn);
    else if (document.readyState === "complete") setTimeout(fn, 0);
    else window.addEventListener("load", () => setTimeout(fn, 0), { once: true });
};

/** The Network Information API, where the browser has it. */
export const currentConnection = (): ConnectionInfo | undefined => (typeof navigator !== "undefined" ? (navigator as Navigator & { connection?: ConnectionInfo }).connection : undefined);
